import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);

/**
 * Format API error detail into a user-friendly string.
 * Handles FastAPI validation errors (array of {msg,...}) and string errors.
 */
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Session role/regions/name/email are cached server-side at login and
  // only refreshed by check-auth — without this poll, a Manager/Agent whose
  // account Admin edits (regions, role, ownership, username, details) would
  // see stale data everywhere until they manually reload or re-login.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') checkAuth();
    }, 20000);
    const onFocus = () => checkAuth();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/check-auth`, {
        withCredentials: true
      });
      if (response.data.authenticated) {
        setAdmin(response.data.user);
        setMustChangePassword(response.data.must_change_password || false);
      } else {
        // Session no longer resolves (e.g. account renamed/deleted while
        // logged in) — clear state so the dashboard's !admin guard redirects
        // to /admin/login instead of leaving a dead, fully-rendered page.
        setAdmin(null);
        setMustChangePassword(false);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setAdmin(null);
        setMustChangePassword(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/login`,
        { username, password },
        { withCredentials: true }
      );
      if (response.data.success) {
        setAdmin(response.data.user);
        setMustChangePassword(response.data.must_change_password || false);
        return { 
          success: true, 
          mustChangePassword: response.data.must_change_password || false 
        };
      }
      return { success: false, error: 'Login failed' };
    } catch (err) {
      // Handle error response properly
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, {
        withCredentials: true
      });
    } catch (err) {
      // Logout API failed - still clear local state
    }
    setAdmin(null);
    setMustChangePassword(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      if (response.data.success) {
        setMustChangePassword(false);
        return { success: true };
      }
      return { success: false, error: response.data.detail || 'Failed to change password' };
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/profile`,
        data,
        { withCredentials: true }
      );
      if (response.data.success) {
        // Refresh admin data
        const meResponse = await axios.get(`${BACKEND_URL}/api/admin/me`, {
          withCredentials: true
        });
        setAdmin(meResponse.data);
        return { success: true };
      }
      return { success: false, error: response.data.detail || 'Failed to update profile' };
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  return (
    <AdminAuthContext.Provider value={{ 
      admin, 
      loading, 
      mustChangePassword,
      login, 
      logout, 
      checkAuth,
      changePassword,
      updateProfile
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
